import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  lodgingMode,
  transportMode,
  travelerFields,
  type BookingConfig,
  type BookingOption,
  type TravelerFields,
} from '@travel/contracts';
import { unwrapRecord, type AdminApiClient, type JsonRecord } from './api-client';
import './booking-config-editor.css';
import { ProductThumbnail } from './product-thumbnail';

export function BookingConfigEditor({
  api,
  productId,
  appId = '',
  value: config,
  onChange: setConfig,
  disabled = false,
  onUploadingChange,
}: {
  api: AdminApiClient;
  productId: string;
  appId?: string;
  value: BookingConfig;
  onChange: (value: BookingConfig) => void;
  disabled?: boolean;
  onUploadingChange?: ((busy: boolean) => void) | undefined;
}) {
  const context = useRef({ api, productId, appId });
  if (
    context.current.api !== api ||
    context.current.productId !== productId ||
    context.current.appId !== appId
  )
    context.current = { api, productId, appId };
  const mounted = useRef(true);
  const gallerySequence = useRef(0);
  const uploadingContext = useRef<typeof context.current>();
  const latestConfig = useRef(config);
  latestConfig.current = config;
  const [media, setMedia] = useState<{ url: string; name: string }[]>([]);
  const [mediaContext, setMediaContext] = useState<typeof context.current>();
  const visibleMedia = mediaContext === context.current ? media : [];
  const [mediaError, setMediaError] = useState('');
  const [uploading, setUploading] = useState(false);
  const lodgingDraft = useRef(config.accommodation);
  const vehicleDrafts = useRef<Partial<Record<'AUTO' | 'CHOICE', BookingOption[]>>>({
    [transportMode(config)]: config.vehicles,
  });
  const isCurrent = (expected: typeof context.current) =>
    mounted.current && context.current === expected;
  async function loadMedia(expected = context.current) {
    const sequence = ++gallerySequence.current;
    let product: JsonRecord;
    try {
      product = unwrapRecord(
        await expected.api.get<JsonRecord>(
          `/api/admin/m2/products/${encodeURIComponent(expected.productId)}`,
        ),
      );
    } catch (error) {
      if (!isCurrent(expected) || sequence !== gallerySequence.current) return;
      throw error;
    }
    if (!isCurrent(expected) || sequence !== gallerySequence.current) return;
    setMediaContext(expected);
    setMedia(
      (Array.isArray(product.media) ? (product.media as JsonRecord[]) : [])
        .filter(
          (m) =>
            m.mediaType === 'IMAGE' &&
            m.status === 'ACTIVE' &&
            (m.appId === expected.appId || m.appId === null),
        )
        .map((m, i) => ({ url: String(m.url), name: String(m.altText || `线路图片 ${i + 1}`) })),
    );
  }
  useEffect(() => {
    mounted.current = true;
    const expected = context.current;
    setMediaError('');
    setUploading(false);
    uploadingContext.current = undefined;
    void loadMedia(expected).catch(() => {
      if (isCurrent(expected)) setMediaError('线路图库读取失败，请重试读取图库。');
    });
    return () => {
      mounted.current = false;
      gallerySequence.current += 1;
    };
  }, [api, productId, appId]);
  useEffect(() => {
    onUploadingChange?.(uploading);
  }, [uploading, onUploadingChange]);
  useEffect(() => () => onUploadingChange?.(false), [onUploadingChange]);
  const imagePicker = (label: string, url: string, change: (url: string) => void) => (
    <div className="booking-media-picker">
      {url && (
        <ProductThumbnail api={api} productId={productId} src={url} alt={label} scopeKey={appId} />
      )}
      <label>
        {label}
        <select value={url} onChange={(e) => change(e.target.value)}>
          <option value="">{label === '下单页头图' ? '使用线路主图' : '不设置配图'}</option>
          {visibleMedia.map((m) => (
            <option key={m.url} value={m.url}>
              {m.name}
            </option>
          ))}
          {url && !visibleMedia.some((m) => m.url === url) && <option value={url}>当前配图</option>}
        </select>
      </label>
      {url && mediaContext === context.current && !visibleMedia.some((m) => m.url === url) && (
        <small>当前配图不在所选小程序的图库中，已有配置会保留；更换时请重新选择或上传。</small>
      )}
      <label className="booking-upload">
        {uploading ? '优化并上传中…' : '上传图片（自动优化）'}
        <input
          type="file"
          accept="image/png,image/jpeg"
          disabled={disabled || uploading || !appId}
          onChange={async (e) => {
            const input = e.currentTarget;
            const file = input.files?.[0];
            const expected = context.current;
            if (!file || disabled || !appId || uploadingContext.current === expected) return;
            if (!['image/png', 'image/jpeg'].includes(file.type) || file.size > 10 * 1024 * 1024) {
              setMediaError('请选择 10MB 内的 JPG / PNG 图片');
              return;
            }
            uploadingContext.current = expected;
            const submittedConfig = JSON.stringify(config);
            setUploading(true);
            setMediaError('');
            try {
              const form = new FormData();
              form.append('file', file);
              form.append('altText', label);
              form.append('sortOrder', String(visibleMedia.length + 1));
              const result = unwrapRecord(
                await api.upload<JsonRecord>(
                  `/api/admin/m2/apps/${encodeURIComponent(appId)}/products/${encodeURIComponent(productId)}/media/upload`,
                  form,
                  crypto.randomUUID(),
                ),
              );
              if (!isCurrent(expected)) return;
              if (typeof result.url === 'string') {
                if (JSON.stringify(latestConfig.current) === submittedConfig) change(result.url);
                else
                  setMediaError(
                    '图片已上传；预订设置已有新修改，请从图库重新选择配图，当前文字已保留。',
                  );
              }
              void loadMedia(expected).catch(() => {
                if (isCurrent(expected))
                  setMediaError('图片已上传，图库刷新失败，请重试读取图库。');
              });
            } catch (error) {
              if (isCurrent(expected))
                setMediaError(error instanceof Error ? error.message : '上传失败，请重试。');
            } finally {
              if (isCurrent(expected)) {
                uploadingContext.current = undefined;
                setUploading(false);
                input.value = '';
              }
            }
          }}
        />
      </label>
    </div>
  );
  const lodging = config.lodging ?? {
    mode: lodgingMode(config),
    summary: config.accommodation[0]?.description ?? '',
    nights: 1,
    roomCapacity: 2,
  };
  const transport = config.transport ?? {
    mode: transportMode(config),
    summary: config.vehicles[0]?.title ?? '',
  };
  const travelerEditor = (child: boolean) => {
    const fields = travelerFields(config, child);
    return (
      <div className="booking-field-rule">
        <strong>{child ? '儿童' : '成人'}</strong>
        {(['name', 'document', 'mobile'] as const)
          .filter((key) => !child || key !== 'mobile')
          .map((key) => (
            <label className="booking-editor__check" key={key}>
              <input
                type="checkbox"
                checked={fields[key]}
                onChange={(e) => {
                  const next: TravelerFields = { ...fields, [key]: e.target.checked };
                  setConfig(
                    child
                      ? { ...config, childTravelers: { ...next, mobile: false } }
                      : { ...config, travelers: next },
                  );
                }}
              />
              {{ name: '姓名', document: '身份证 / 护照', mobile: '手机号' }[key]}
            </label>
          ))}
        <span>{child ? '儿童联系使用订单联系人' : '仅勾选履约必需的信息'}</span>
      </div>
    );
  };
  return (
    <section className="booking-editor booking-editor--integrated">
      <h2>服务与下单规则</h2>
      <p>默认每天可售，停售请下架线路。退款申请由客服人工审核。</p>
      <fieldset disabled={disabled || uploading}>
        <Row title="人数与儿童">
          <label className="booking-editor__check">
            <input
              type="checkbox"
              checked={config.children.enabled}
              onChange={(e) =>
                setConfig({
                  ...config,
                  children: { ...config.children, enabled: e.target.checked },
                })
              }
            />
            允许儿童预订
          </label>
          {config.children.enabled && (
            <>
              <Money
                label="儿童基础价（元 / 人）"
                value={config.children.priceCents}
                onChange={(priceCents) =>
                  setConfig({ ...config, children: { ...config.children, priceCents } })
                }
                placeholder="与成人同价"
              />
              <label>
                年龄下限
                <input
                  type="number"
                  min="0"
                  max="17"
                  value={config.children.minAge ?? ''}
                  placeholder="未限定"
                  onChange={(e) => {
                    const children = { ...config.children };
                    if (e.target.value === '') delete children.minAge;
                    else children.minAge = Number(e.target.value);
                    setConfig({ ...config, children });
                  }}
                />
              </label>
              <label>
                年龄上限
                <input
                  type="number"
                  min="0"
                  max="17"
                  value={config.children.maxAge ?? ''}
                  placeholder="未限定"
                  onChange={(e) => {
                    const children = { ...config.children };
                    if (e.target.value === '') delete children.maxAge;
                    else children.maxAge = Number(e.target.value);
                    setConfig({ ...config, children });
                  }}
                />
              </label>
              <label className="booking-editor__grow">
                儿童费用说明
                <input
                  value={config.children.description}
                  maxLength={200}
                  placeholder="如：不占床，不含早餐"
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      children: { ...config.children, description: e.target.value },
                    })
                  }
                />
              </label>
            </>
          )}
        </Row>
        <Row title="必要信息采集">
          {travelerEditor(false)}
          {config.children.enabled && travelerEditor(true)}
        </Row>
        <Row title="接送与集合">
          <label>
            服务方式
            <select
              value={config.pickup.mode}
              onChange={(e) =>
                setConfig({
                  ...config,
                  pickup: {
                    ...config.pickup,
                    mode: e.target.value as BookingConfig['pickup']['mode'],
                    collectTime: false,
                  },
                })
              }
            >
              <option value="NONE">不提供接送</option>
              <option value="MEETING_POINT">游客自行集合</option>
              <option value="HOTEL">酒店 / 地址接送</option>
            </select>
          </label>
          {config.pickup.mode !== 'NONE' && (
            <label className="booking-editor__grow">
              {config.pickup.mode === 'MEETING_POINT' ? '集合地点与时间' : '接送范围'}
              <input
                value={config.pickup.description}
                placeholder={
                  config.pickup.mode === 'MEETING_POINT'
                    ? '如：08:00 景区东门集合'
                    : '如：景洪市区酒店免费接送'
                }
                onChange={(e) =>
                  setConfig({
                    ...config,
                    pickup: { ...config.pickup, description: e.target.value },
                  })
                }
              />
            </label>
          )}
          {config.pickup.mode === 'HOTEL' && (
            <label className="booking-editor__check">
              <input
                type="checkbox"
                checked={config.pickup.collectTime}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    pickup: { ...config.pickup, collectTime: e.target.checked },
                  })
                }
              />
              需要游客选择期望接送时间
            </label>
          )}
        </Row>
        <Row title="住宿">
          <label>
            住宿方式
            <select
              value={lodging.mode}
              onChange={(e) => {
                if (lodging.mode === 'CHOICE') lodgingDraft.current = config.accommodation;
                setConfig({
                  ...config,
                  lodging: { ...lodging, mode: e.target.value as typeof lodging.mode },
                  accommodation:
                    e.target.value === 'NONE' || e.target.value === 'INCLUDED'
                      ? []
                      : lodgingDraft.current,
                });
              }}
            >
              <option value="NONE">不含住宿</option>
              <option value="INCLUDED">固定住宿，已包含</option>
              <option value="CHOICE">可选住宿方案</option>
            </select>
          </label>
          {lodging.mode !== 'NONE' && (
            <>
              <label>
                住宿晚数
                <input
                  type="number"
                  min="1"
                  max="60"
                  value={lodging.nights}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      lodging: { ...lodging, nights: Number(e.target.value) },
                    })
                  }
                />
              </label>
              <label>
                每间入住人数
                <input
                  type="number"
                  min="1"
                  max="8"
                  value={lodging.roomCapacity}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      lodging: { ...lodging, roomCapacity: Number(e.target.value) },
                    })
                  }
                />
              </label>
              <label className="booking-editor__grow">
                住宿说明
                <input
                  value={lodging.summary}
                  placeholder="如：舒适型酒店标准间"
                  onChange={(e) =>
                    setConfig({ ...config, lodging: { ...lodging, summary: e.target.value } })
                  }
                />
              </label>
              {config.children.enabled && (
                <>
                  <label>
                    儿童占床
                    <select
                      value={config.children.bed ?? 'NONE'}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          children: {
                            ...config.children,
                            bed: e.target.value as 'NONE' | 'ADULT_PRICE' | 'SUPPLEMENT',
                          },
                        })
                      }
                    >
                      <option value="NONE">不提供占床选择</option>
                      <option value="ADULT_PRICE">占床按成人价</option>
                      <option value="SUPPLEMENT">占床补差价</option>
                    </select>
                  </label>
                  {config.children.bed === 'SUPPLEMENT' && (
                    <Money
                      label="每名儿童占床补差（元）"
                      value={config.children.bedSupplementCents ?? 0}
                      onChange={(bedSupplementCents) =>
                        setConfig({
                          ...config,
                          children: {
                            ...config.children,
                            bedSupplementCents: bedSupplementCents ?? 0,
                          },
                        })
                      }
                    />
                  )}
                </>
              )}
            </>
          )}
          {lodging.mode === 'CHOICE' && (
            <OptionEditor
              title="住宿方案"
              options={config.accommodation}
              onChange={(accommodation) => setConfig({ ...config, accommodation })}
              imagePicker={imagePicker}
            />
          )}
        </Row>
        <Row title="车辆安排">
          <label>
            安排方式
            <select
              value={transport.mode}
              onChange={(e) => {
                if (transport.mode === 'AUTO' || transport.mode === 'CHOICE')
                  vehicleDrafts.current[transport.mode] = config.vehicles;
                const next = e.target.value as typeof transport.mode;
                setConfig({
                  ...config,
                  transport: { ...transport, mode: next },
                  vehicles:
                    next === 'NONE' || next === 'INCLUDED'
                      ? []
                      : (vehicleDrafts.current[next] ??
                        config.vehicles.map((v) => ({
                          ...v,
                          ...(next === 'AUTO' ? { priceCents: 0 } : {}),
                        }))),
                });
              }}
            >
              <option value="NONE">无需车辆安排</option>
              <option value="INCLUDED">固定车型，已包含</option>
              <option value="AUTO">按人数自动配车，已包含</option>
              <option value="CHOICE">展示车型，用户自行选择</option>
            </select>
          </label>
          {transport.mode !== 'NONE' && (
            <label className="booking-editor__grow">
              车辆说明
              <input
                aria-label="车辆说明"
                value={transport.summary}
                placeholder="车辆安排将显示在人数下方"
                onChange={(e) =>
                  setConfig({ ...config, transport: { ...transport, summary: e.target.value } })
                }
              />
            </label>
          )}
          {['AUTO', 'CHOICE'].includes(transport.mode) && (
            <OptionEditor
              title={transport.mode === 'AUTO' ? '配车规则' : '车型'}
              auto={transport.mode === 'AUTO'}
              capacity
              options={config.vehicles}
              onChange={(vehicles) => setConfig({ ...config, vehicles })}
              imagePicker={imagePicker}
            />
          )}
        </Row>
        <Row title="增值服务">
          <OptionEditor
            title="可选服务"
            allowRoomNight={lodging.mode !== 'NONE'}
            options={config.extras}
            onChange={(extras) => setConfig({ ...config, extras })}
            imagePicker={imagePicker}
          />
          <small>前端默认不勾选，用户明确选择后计价。</small>
        </Row>
        <Row title="页面素材">
          {imagePicker('下单页头图', config.hero?.imageUrl ?? '', (imageUrl) =>
            setConfig({ ...config, hero: { title: '', subtitle: '', ...config.hero, imageUrl } }),
          )}
          <label>
            主标题
            <textarea
              rows={2}
              maxLength={50}
              value={config.hero?.title ?? ''}
              placeholder="留空使用线路名称"
              onChange={(e) =>
                setConfig({
                  ...config,
                  hero: { imageUrl: '', subtitle: '', ...config.hero, title: e.target.value },
                })
              }
            />
          </label>
          <label>
            副标题
            <input
              maxLength={100}
              value={config.hero?.subtitle ?? ''}
              placeholder="留空使用线路简介"
              onChange={(e) =>
                setConfig({
                  ...config,
                  hero: { imageUrl: '', title: '', ...config.hero, subtitle: e.target.value },
                })
              }
            />
          </label>
        </Row>
        {mediaError && (
          <p role="alert">
            {mediaError}{' '}
            <button
              type="button"
              disabled={uploading || disabled}
              onClick={() => {
                setMediaError('');
                const expected = context.current;
                void loadMedia(expected).catch(() => {
                  if (isCurrent(expected)) setMediaError('线路图库读取失败，请重试。');
                });
              }}
            >
              重试读取图库
            </button>
          </p>
        )}
      </fieldset>
    </section>
  );
}
function Row({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="booking-editor__row">
      <h3>{title}</h3>
      <div className="booking-editor__fields">{children}</div>
    </div>
  );
}
function Money({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
  placeholder?: string;
}) {
  return (
    <label>
      {label}
      <input
        type="number"
        min="0"
        step="0.01"
        placeholder={placeholder}
        value={value === null ? '' : value / 100}
        onChange={(e) =>
          onChange(e.target.value === '' ? null : Math.round(Number(e.target.value) * 100))
        }
      />
    </label>
  );
}
function OptionEditor({
  title,
  options,
  onChange,
  imagePicker,
  auto = false,
  capacity = false,
  allowRoomNight = false,
}: {
  title: string;
  options: BookingOption[];
  onChange: (options: BookingOption[]) => void;
  imagePicker: (label: string, url: string, onChange: (url: string) => void) => ReactNode;
  auto?: boolean;
  capacity?: boolean;
  allowRoomNight?: boolean;
}) {
  const patch = (id: string, values: Partial<BookingOption>) =>
    onChange(options.map((o) => (o.id === id ? { ...o, ...values } : o)));
  return (
    <div className="booking-editor__options">
      {options.map((option) => (
        <div className="booking-editor__option" key={option.id}>
          {imagePicker(`${title}配图`, option.imageUrl ?? '', (imageUrl) =>
            patch(option.id, { imageUrl }),
          )}
          <label>
            名称
            <input
              maxLength={60}
              value={option.title}
              onChange={(e) => patch(option.id, { title: e.target.value })}
            />
          </label>
          <label className="booking-editor__grow">
            说明
            <input
              maxLength={200}
              value={option.description}
              onChange={(e) => patch(option.id, { description: e.target.value })}
            />
          </label>
          {(auto || capacity) && (
            <>
              <label>
                最少人数
                <input
                  type="number"
                  min="1"
                  max="99"
                  value={option.minPeople ?? 1}
                  onChange={(e) => patch(option.id, { minPeople: Number(e.target.value) })}
                />
              </label>
              <label>
                最多人数
                <input
                  type="number"
                  min="1"
                  max="99"
                  value={option.maxPeople ?? (auto ? 4 : 99)}
                  onChange={(e) => patch(option.id, { maxPeople: Number(e.target.value) })}
                />
              </label>
            </>
          )}
          {!auto && (
            <>
              <Money
                label="加价（元）"
                value={option.priceCents}
                onChange={(priceCents) => patch(option.id, { priceCents: priceCents ?? 0 })}
              />
              <label>
                计费单位
                <select
                  value={option.charge}
                  onChange={(e) =>
                    patch(option.id, { charge: e.target.value as BookingOption['charge'] })
                  }
                >
                  <option value="ORDER">每单</option>
                  <option value="PERSON">每位出行人</option>
                  <option value="ADULT">每名成人</option>
                  {(title === '住宿方案' || allowRoomNight || option.charge === 'ROOM_NIGHT') && (
                    <option value="ROOM_NIGHT">每间每晚</option>
                  )}
                </select>
              </label>
            </>
          )}
          <button type="button" onClick={() => onChange(options.filter((o) => o.id !== option.id))}>
            移除
          </button>
        </div>
      ))}
      <button
        type="button"
        disabled={options.length >= 20}
        onClick={() =>
          onChange([
            ...options,
            {
              id: crypto.randomUUID(),
              title: '',
              description: '',
              priceCents: 0,
              charge: 'ORDER',
              ...(auto || capacity ? { minPeople: 1, maxPeople: 4 } : {}),
            },
          ])
        }
      >
        ＋ 添加{title}
      </button>
      {options.length > 0 && !auto && <small>0 元表示套餐已含；付费选项须由用户主动选择。</small>}
    </div>
  );
}
